<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect, nextTick } from 'vue'
import type { Note } from '../../types/bookmark'
import type { BibleVersion, BibleBookOption } from '../../types/bible'
import { useGlobalNotes } from '../../composables/useGlobalNotes'
import { useGlobalBibleData } from '../../composables/useGlobalBibleData'
import { useFrameStore } from '../../stores/frameStore'
import MarkdownToolbar from '../MarkdownToolbar.vue'
import { renderMarkdownPreview } from '../../utils/markdownRenderer'
import { getListContinuationEdit } from '../../utils/listContinuation'

interface Props {
  frameId: number
  versionCode?: string
  bookId?: number
  chapterNumber?: number
  title?: string
  isReadingMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '筆記編輯器',
  isReadingMode: false,
})

const displayTitle = computed(() => props.title || '筆記編輯器')

const { notes, currentNote, createNote, updateNote, deleteNote, selectNote, loadNotesByVerse } = useGlobalNotes()
const { versions, books, loadVersions, loadBooks } = useGlobalBibleData()
const frameStore = useFrameStore()

const mode = ref<'editor' | 'list'>('editor')
// 閱讀模式時: isEditing = false (預覽全屏，不能編輯)
// 編輯模式時: isEditing = true (預覽50:50，可編輯)
const isEditing = ref(props.isReadingMode === false)

// 調試日誌
console.log('NoteEditorFrame 初始化', {
  frameId: props.frameId,
  isReadingMode: props.isReadingMode,
  isEditingLogic: `isReadingMode === false = ${props.isReadingMode === false}`,
  isEditing: isEditing.value,
  versionCode: props.versionCode,
  bookId: props.bookId,
  chapterNumber: props.chapterNumber,
})
const textareaRef = ref<HTMLTextAreaElement>()
const editorContent = ref('')
const autosaveStatus = ref('')

const frameNotes = computed(() => notes.value)

const getVerseInfo = (note: Note): string => {
  const version = versions.value.find(v => v.code === note.versionCode)
  const book = books.value.find(b => b.id === note.bookId)

  if (!version || !book) {
    return '筆記'
  }

  return `${book.chiFull}${note.chapterNumber}:${note.verseStart}（${version.name}）`
}

const previewHtml = computed(() => {
  return renderMarkdownPreview(editorContent.value)
})

const handleInsertMarkdown = (syntax: string) => {
  if (!textareaRef.value) return
  const start = textareaRef.value.selectionStart
  const end = textareaRef.value.selectionEnd
  const selected = editorContent.value.substring(start, end)
  const before = editorContent.value.substring(0, start)
  const after = editorContent.value.substring(end)
  editorContent.value = `${before}${syntax}${selected}${after}`
  textareaRef.value.focus()
  textareaRef.value.setSelectionRange(start + syntax.length, start + syntax.length + selected.length)
}

const handleEditorKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter') return
  const textarea = textareaRef.value
  if (!textarea) return

  const edit = getListContinuationEdit(editorContent.value, textarea.selectionStart)
  if (!edit) return

  event.preventDefault()
  editorContent.value = edit.newValue
  nextTick(() => {
    textarea.setSelectionRange(edit.newCursorPos, edit.newCursorPos)
  })
}

const selectNoteAndEdit = (note: Note) => {
  selectNote(note)
  editorContent.value = note.content
  mode.value = 'editor'
  isEditing.value = true
}

const selectNoteAndView = async (note: Note) => {
  selectNote(note)
  editorContent.value = note.content
  mode.value = 'editor'
  isEditing.value = false

  // 確保該版本的書籍資料被加載
  if (!books.value.some(b => b.id === note.bookId)) {
    await loadBooks(note.versionCode)
  }

  // 更新框架的標題為該筆記的經文信息
  const frame = frameStore.frames.find(f => f.frameId === props.frameId)
  if (frame) {
    const book = books.value.find(b => b.id === note.bookId)
    const version = versions.value.find(v => v.code === note.versionCode)
    const bookName = book?.chiFull || '未知'
    const versionName = version?.name || '未知'
    frame.title = `${bookName}${note.chapterNumber}:${note.verseStart}（${versionName}）`
  }
}

const toggleEditing = () => {
  isEditing.value = !isEditing.value
}

const handleDeleteNote = async (noteId: number) => {
  if (!confirm('確定刪除此筆記？')) return
  try {
    await deleteNote(noteId)
    if (currentNote.value?.id === noteId) {
      editorContent.value = ''
      if (frameNotes.value.length > 0) {
        selectNoteAndEdit(frameNotes.value[0])
      }
    }
  } catch (error) {
    console.error('Failed to delete note:', error)
  }
}

const handleSaveNote = async () => {
  if (!currentNote.value) {
    autosaveStatus.value = '無可用筆記'
    return
  }

  try {
    autosaveStatus.value = '保存中...'
    await updateNote(currentNote.value.id, editorContent.value)
    autosaveStatus.value = `已保存於 ${new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}`
    setTimeout(() => {
      autosaveStatus.value = ''
    }, 3000)
  } catch (error) {
    console.error('Failed to save note:', error)
    autosaveStatus.value = '保存失敗'
  }
}

watch(
  () => currentNote.value,
  (newNote) => {
    if (newNote) {
      editorContent.value = newNote.content
    } else {
      editorContent.value = ''
    }
  }
)

watch(
  () => editorContent.value,
  (newContent) => {
    if (currentNote.value) {
      currentNote.value = { ...currentNote.value, content: newContent }
    }
  }
)

onMounted(async () => {
  // 加載全局聖經數據（版本和書籍），供 getVerseInfo 使用
  await loadVersions()
  // 加載所有版本的書籍資訊，以便在筆記列表中正確顯示經文
  for (const version of versions.value) {
    await loadBooks(version.code)
  }
  if (props.versionCode) {
    await loadBooks(props.versionCode)
  }
})

watchEffect(async () => {
  if (!props.versionCode || props.bookId === undefined || props.chapterNumber === undefined) {
    return
  }

  // 首先嘗試加載該經文的筆記
  try {
    const verseNotes = await loadNotesByVerse(props.versionCode, props.bookId, props.chapterNumber)
    if (verseNotes && verseNotes.length > 0) {
      selectNote(verseNotes[0])
      editorContent.value = verseNotes[0].content
      mode.value = 'editor'
      return
    }
  } catch (error) {
    console.error('Failed to load notes for verse:', error)
  }

  // 如果該經文沒有筆記，檢查全局 currentNote 是否屬於該經文
  if (currentNote.value &&
      currentNote.value.versionCode === props.versionCode &&
      currentNote.value.bookId === props.bookId &&
      currentNote.value.chapterNumber === props.chapterNumber) {
    editorContent.value = currentNote.value.content
    mode.value = 'editor'
    return
  }

  // 否則顯示空編輯器（等待用戶輸入後保存）
  selectNote(null as any)
  editorContent.value = ''
  mode.value = 'editor'
})
</script>

<template>
  <div class="note-editor-frame">
    <!-- 工具列第一層：模式選擇 -->
    <div class="toolbar-first-layer">
      <button :class="{ active: mode === 'editor' }" @click="mode = 'editor'">
        筆記
      </button>
      <button :class="{ active: mode === 'list' }" @click="mode = 'list'">
        筆記列表 ({{ frameNotes.length }})
      </button>
    </div>

    <!-- 編輯器區域 -->
    <div v-if="mode === 'editor'" class="editor-section">
      <!-- 工具列第二層：編輯功能 -->
      <div class="toolbar-second-layer">
        <MarkdownToolbar :disabled="!isEditing" @insert-markdown="handleInsertMarkdown" />
        <div class="flex-spacer" />
        <button
          :class="{ active: isEditing }"
          :disabled="!currentNote"
          class="btn btn-edit"
          @click="toggleEditing"
          title="切換編輯狀態"
        >
          {{ isEditing ? '☑' : '☐' }} 編輯
        </button>
        <button
          :disabled="!isEditing || !currentNote"
          class="btn btn-primary btn-save"
          @click="handleSaveNote"
        >
          保存
        </button>
      </div>

      <!-- 編輯區：根據 isEditing 動態調整布局 -->
      <div v-if="isEditing" class="editor-container-split">
        <!-- 編輯模式：編輯區 50% + 預覽區 50% -->
        <textarea
          ref="textareaRef"
          v-model="editorContent"
          class="editor"
          placeholder="輸入筆記內容..."
          @keydown="handleEditorKeydown"
        />
        <div class="preview" v-html="previewHtml" />
      </div>

      <div v-else class="editor-container-fullscreen">
        <!-- 閱讀模式：預覽區 100%（編輯區隱藏） -->
        <div class="preview preview-fullscreen" v-html="previewHtml" />
      </div>

      <div v-if="isEditing" class="status">{{ autosaveStatus }}</div>
    </div>

    <!-- 列表區域 -->
    <div v-if="mode === 'list'" class="list-panel">
      <div v-if="frameNotes.length === 0" class="empty-state">
        暫無筆記
      </div>
      <div v-for="note in frameNotes" :key="note.id" class="note-item">
        <div class="note-content">
          <div class="note-verse">{{ getVerseInfo(note) }}</div>
          <div class="note-time">{{ new Date(note.updatedAt).toLocaleString('zh-TW') }}</div>
        </div>
        <div class="note-actions">
          <button class="btn btn-small" @click="selectNoteAndView(note)">閱讀</button>
          <button class="btn btn-small btn-danger" @click="handleDeleteNote(note.id)">刪除</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.note-editor-frame {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
}

.toolbar-first-layer {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  min-height: 34px;
}

.toolbar-first-layer button {
  padding: 6px 12px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  transition: all 0.2s;
  white-space: nowrap;
}

.toolbar-first-layer button.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  font-weight: 600;
}

.toolbar-first-layer button:hover {
  color: #333;
}

.toolbar-second-layer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  min-height: 40px;
  flex-wrap: wrap;
}

.flex-spacer {
  flex: 1;
}

.editor-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.editor-container-split {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.btn-edit {
  padding: 4px 8px;
  font-size: 12px;
  white-space: nowrap;
}

.btn-edit.active {
  background-color: #e3f2fd;
  border-color: #2563eb;
  color: #2563eb;
}

.btn-save {
  padding: 4px 12px;
  font-size: 12px;
  white-space: nowrap;
}

.editor {
  flex: 1;
  padding: 12px;
  border: none;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: none;
  color: #333;
  outline: none;
}

.editor-container-fullscreen {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.preview {
  flex: 1;
  padding: 12px;
  border-left: 1px solid #e0e0e0;
  overflow-y: auto;
  background-color: #f9f9f9;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.preview-fullscreen {
  flex: 1;
  padding: 12px;
  border: none;
  overflow-y: auto;
  background-color: #fff;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.preview :deep(h1) {
  font-size: 20px;
  font-weight: 600;
  margin: 8px 0;
}

.preview :deep(h2) {
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0;
}

.preview :deep(h3) {
  font-size: 16px;
  font-weight: 600;
  margin: 8px 0;
}

.preview :deep(strong) {
  font-weight: 600;
}

.preview :deep(em) {
  font-style: italic;
}

.preview :deep(p) {
  margin: 0 0 4px 0;
}

.preview :deep(ul) {
  margin: 4px 0;
  padding-left: 24px;
  list-style-type: disc;
  display: block;
}

.preview :deep(ol) {
  margin: 4px 0;
  padding-left: 24px;
  list-style-type: decimal;
  display: block;
}

.preview :deep(li) {
  margin: 2px 0;
  display: list-item;
}

.status {
  padding: 6px 12px;
  font-size: 11px;
  color: #999;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
  min-height: 20px;
}

.list-panel {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 13px;
}

.note-item {
  display: flex;
  gap: 8px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  background-color: #fafafa;
}

.note-content {
  flex: 1;
  min-width: 0;
}

.note-verse {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 2px;
}

.note-preview {
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.note-time {
  font-size: 11px;
  color: #999;
}

.note-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.btn {
  padding: 4px 8px;
  border: 1px solid #d0d0d0;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
  background-color: white;
  color: #333;
}

.btn:hover:not(:disabled) {
  background-color: #f0f0f0;
  border-color: #999;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5;
  color: #999;
  border-color: #ddd;
}

.btn-small {
  padding: 3px 6px;
  font-size: 10px;
  min-width: 50px;
}

.btn-primary {
  background-color: #2563eb;
  color: white;
  border-color: #1d4ed8;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

.btn-danger {
  background-color: #ff4444;
  color: white;
  border-color: #cc0000;
}

.btn-danger:hover {
  background-color: #cc0000;
}
</style>
